type JsonObject = Record<string, unknown>;

export function passkeysSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'PublicKeyCredential' in window &&
    Boolean(navigator.credentials)
  );
}

export async function createPasskey(options: JsonObject): Promise<JsonObject> {
  const credential = await navigator.credentials.create({
    publicKey: creationOptionsFromJson(options),
  });
  if (!(credential instanceof PublicKeyCredential))
    throw new Error('Passkey creation was canceled.');
  return credentialToJson(credential);
}

export async function getPasskey(options: JsonObject): Promise<JsonObject> {
  const credential = await navigator.credentials.get({
    publicKey: requestOptionsFromJson(options),
  });
  if (!(credential instanceof PublicKeyCredential))
    throw new Error('Passkey sign-in was canceled.');
  return credentialToJson(credential);
}

export function credentialToJson(credential: PublicKeyCredential): JsonObject {
  const response = credential.response;
  const common = {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  };

  if (response instanceof AuthenticatorAttestationResponse) {
    return {
      ...common,
      response: {
        clientDataJSON: toBase64Url(response.clientDataJSON),
        attestationObject: toBase64Url(response.attestationObject),
        transports: response.getTransports(),
        publicKeyAlgorithm: response.getPublicKeyAlgorithm(),
        ...(response.getPublicKey() ? { publicKey: toBase64Url(response.getPublicKey()!) } : {}),
        authenticatorData: toBase64Url(response.getAuthenticatorData()),
      },
    };
  }

  if (response instanceof AuthenticatorAssertionResponse) {
    return {
      ...common,
      response: {
        clientDataJSON: toBase64Url(response.clientDataJSON),
        authenticatorData: toBase64Url(response.authenticatorData),
        signature: toBase64Url(response.signature),
        userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
      },
    };
  }

  throw new Error('The browser returned an unsupported passkey response.');
}

function creationOptionsFromJson(value: JsonObject): PublicKeyCredentialCreationOptions {
  const options = structuredClone(value) as {
    challenge: string;
    user: { id: string };
    excludeCredentials?: Array<{ id: string }>;
  };
  return {
    ...options,
    challenge: fromBase64Url(options.challenge),
    user: { ...options.user, id: fromBase64Url(options.user.id) },
    ...(options.excludeCredentials
      ? {
          excludeCredentials: options.excludeCredentials.map((credential) => ({
            ...credential,
            id: fromBase64Url(credential.id),
          })),
        }
      : {}),
  } as PublicKeyCredentialCreationOptions;
}

function requestOptionsFromJson(value: JsonObject): PublicKeyCredentialRequestOptions {
  const options = structuredClone(value) as {
    challenge: string;
    allowCredentials?: Array<{ id: string }>;
  };
  return {
    ...options,
    challenge: fromBase64Url(options.challenge),
    ...(options.allowCredentials
      ? {
          allowCredentials: options.allowCredentials.map((credential) => ({
            ...credential,
            id: fromBase64Url(credential.id),
          })),
        }
      : {}),
  } as PublicKeyCredentialRequestOptions;
}

function fromBase64Url(value: string): ArrayBuffer {
  const base64 = value
    .replaceAll('-', '+')
    .replaceAll('_', '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  return bytes.buffer;
}

function toBase64Url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}
